import Panel from '../Panel';
import { Typography } from '@mui/material';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/system';

const quartileMapping = {
    4: 'Top Quartile',
    3: 'Upper Middle',
    2: 'Lower Middle',
    1: 'Bottom Quartile'
};

/**
 * Get the correct ordinal suffix (-nd, -rd, -st, -th) for a given integer
 * @param {number} i
 * @returns {string}
 */
function ordinalSuffixOf(i) {
    let j = i % 10,
        k = i % 100;
    if (j === 1 && k !== 11) {
        return 'st';
    }
    if (j === 2 && k !== 12) {
        return 'nd';
    }
    if (j === 3 && k !== 13) {
        return 'rd';
    }
    return 'th';
}

/**
 * Renders a responsive SVG to graphically represent a peer group quartile rank,
 * consisting of 4 stacked bars, one highlighted and displaying a corresponding BA score percentile.
 *
 * @component
 * @param {Number} fontSize - The fontsize to use for text
 * @param {Number} percentile - Percentile rank
 * @param quartile - Quartile Rank
 * @param showPercentileText - True if displaying 'Percentile' text used for larger format markers
 * @returns {React.ReactElement} The rendered ProQuartileRank component
 */
export const QuartileMarker = ({
    fontSize,
    percentile,
    quartile,
    showPercentileText
}) => {
    const theme = useTheme();
    const yQuartileShift = (4 - quartile) * 21.5 + 10;
    const yPercentileTextShift = showPercentileText ? 3 : 0;

    return (
        <>
            {/*4 quartile bars*/}
            {[...Array(4)].map((e, i) => (
                <rect
                    x={'2.5%'}
                    y={i * 21.5 + 10 + '%'}
                    width='95%'
                    height='17%'
                    rx='12%'
                    fill={theme.palette.quartileBars[i].unselected}
                    key={quartileMapping[i + 1]}
                />
            ))}
            {/*bar for relevant quartile with percentile text*/}
            {percentile && quartile && (
                <>
                    <circle
                        cx='50%'
                        cy={8.5 + yQuartileShift + '%'}
                        r='18%'
                        fill={theme.palette.quartileBars[4 - quartile].selected}
                        strokeWidth={'5%'}
                        stroke={theme.palette.common.white}
                    />
                    <rect
                        y={yQuartileShift + '%'}
                        x={'2.5%'}
                        width='95%'
                        height='17%'
                        rx='12%'
                        fill={theme.palette.quartileBars[4 - quartile].selected}
                    />
                    <text
                        x={showPercentileText ? '50%' : '52%'}
                        y={8.5 - yPercentileTextShift + yQuartileShift + '%'}
                        style={{
                            fill: theme.palette.common.white,
                            fontSize: fontSize,
                            fontWeight: showPercentileText ? 500 : 400,
                            textAnchor: 'middle',
                            dominantBaseline: 'central'
                        }}
                    >
                        {Math.round(percentile)}
                        <tspan fontSize={fontSize * 0.65} dy='-2.5%'>
                            {ordinalSuffixOf(Math.round(percentile))}
                        </tspan>
                    </text>
                    {showPercentileText && (
                        <text
                            x={'50%'}
                            y={13 + yQuartileShift + '%'}
                            style={{
                                fill: theme.palette.common.white,
                                fontSize: fontSize * 0.7,
                                textAnchor: 'middle'
                            }}
                        >
                            Percentile
                        </text>
                    )}
                </>
            )}
        </>
    );
};

QuartileMarker.propTypes = {
    fontSize: PropTypes.number,
    percentile: PropTypes.number,
    quartile: PropTypes.number,
    showPercentileText: PropTypes.bool
};

/**
 * Renders a panel containing a graphical representation of a percentile rank score.
 * This consists of 4 stacked bars, with one highlighted bar showing the relevant percentile.
 *
 * Also renders a title and text showing the quartile (1st, 2nd, 3rd, 4th).
 *
 * @component
 * @param {Object} peerGroupData - calculation data for the peer group (Overall skill)
 * @returns {React.ReactElement} The rendered ProQuartileRank component
 */
const ProQuartileRank = ({ peerGroupData }) => {
    const theme = useTheme();
    return (
        <Panel
            sx={{
                alignItems: 'center',
                flexDirection: 'column',
                paddingBottom: '1.5rem',
                paddingTop: { xs: 0, sm: '2rem', md: '2.5rem' },
                height: '100%'
            }}
            data-analyticsid='portfolio-drilldown-rank'
        >
            <Typography textAlign={'center'} variant='h2'>
                Quartile Rank
            </Typography>
            <Typography
                variant='body1'
                textAlign={'center'}
                color={theme.palette.font.light}
                sx={{ paddingBottom: !peerGroupData && 3 }}
            >
                {quartileMapping[peerGroupData?.quartile]}
            </Typography>

            <Box
                height='100%'
                width='100%'
                display='flex'
                justifyContent='center'
                alignItems='center'
                maxWidth={200}
                paddingLeft={{ sm: 3, md: 2.5 }}
                paddingRight={{ sm: 3, md: 2.5 }}
                data-analyticsid='rank-icon'
            >
                <svg
                    id='quartile bars'
                    width='100%'
                    height='100%'
                    viewBox='0 0 165 207'
                >
                    <QuartileMarker
                        x={0}
                        fontSize={17}
                        showPercentileText={true}
                        {...peerGroupData}
                    />
                </svg>
            </Box>
        </Panel>
    );
};

ProQuartileRank.propTypes = {
    peerGroupData: PropTypes.object,
    maxWidth: PropTypes.number
};

export default ProQuartileRank;
